/**
 * Le mie leghe (multi-lega fase 3): elenco delle leghe di cui si fa parte,
 * creazione di una nuova lega, ingresso con un codice, codice di ingresso
 * della lega corrente (per il presidente / admin).
 */
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { AxiosError } from "axios";
import { useAuth } from "../../hooks/useAuth";
import {
  annoFantaCorrente,
  useCambiaLega,
  useCreaLega,
  useEntraLega,
  useLegaCorrente,
  useLegheMie,
  useRigeneraCodice,
} from "../../hooks/useLeghe";
import { AvvisoMantra } from "../common/AvvisoMantra";
import { etichetta } from "../common/etichette";
import { MobileShell } from "../shell/MobileShell";
import { NOME_MODALITA_PRO } from "../common/nomenclatura";
import { tr } from "../../i18n";

const INPUT = "w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg";
const LABEL = "block text-xs text-fg-muted uppercase tracking-wider mb-1";

function messaggioErrore(e: unknown): string {
  if (e instanceof AxiosError) {
    const detail = (e.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const primo = detail[0] as { msg?: string };
      if (primo?.msg) return primo.msg;
    }
  }
  return tr("Qualcosa è andato storto, riprova.");
}

export function LeghePage() {
  const { user } = useAuth();
  const { data: mie, isLoading } = useLegheMie();
  const { data: corrente } = useLegaCorrente();
  const cambia = useCambiaLega();
  const crea = useCreaLega();
  const entra = useEntraLega();
  const rigenera = useRigeneraCodice();

  const [esito, setEsito] = useState<{ tipo: "ok" | "errore"; testo: string } | null>(null);

  // --- form crea lega ---
  const [nome, setNome] = useState("");
  const [modalita, setModalita] = useState<"mantra" | "classic">("mantra");
  const [anno, setAnno] = useState(annoFantaCorrente());
  const [nSquadre, setNSquadre] = useState(8);
  const [nomeSquadra, setNomeSquadra] = useState("");
  const [saInizio, setSaInizio] = useState(1);
  // Andrea 2026-10-06: la lega parte dalla prossima giornata di Serie A (non
  // dalla 1, che a stagione iniziata lascerebbe giornate passate e vuote).
  const { data: infoReg } = useQuery({
    queryKey: ["registrazione-info"],
    queryFn: async () =>
      (await api.get<{ prossima_giornata_sa: number | null }>("/auth/registrazione")).data,
    staleTime: 60 * 60 * 1000,
  });
  const saToccato = useRef(false);
  useEffect(() => {
    const g = infoReg?.prossima_giornata_sa;
    if (g && !saToccato.current) setSaInizio(g);
  }, [infoReg?.prossima_giornata_sa]);
  // Da registrazione ("Crea una lega"): si apre direttamente sul form
  const [params] = useSearchParams();
  const daRegistrazione = params.get("crea") === "1";
  const formCreaRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    if (daRegistrazione)
      formCreaRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [daRegistrazione]);
  const [saFine, setSaFine] = useState(38);
  const [budgetStag, setBudgetStag] = useState(150);
  const [budgetInv, setBudgetInv] = useState(50);
  const [mostraAvanzate, setMostraAvanzate] = useState(false);

  // --- form entra ---
  const [codice, setCodice] = useState("");
  const [nomeSquadraIngresso, setNomeSquadraIngresso] = useState("");

  const membroCorrente = corrente?.membro;
  const isAdminCorrente =
    !!membroCorrente &&
    (["presidente_lega", "admin"].includes(membroCorrente.ruolo_lega) ||
      membroCorrente.ruoli_aggiuntivi.some((r) => r === "presidente_lega" || r === "admin"));
  const isPresidenteCorrente = membroCorrente?.ruolo_lega === "presidente_lega";

  async function onCrea(e: React.FormEvent) {
    e.preventDefault();
    setEsito(null);
    try {
      const creata = await crea.mutateAsync({
        nome: nome.trim(),
        modalita,
        anno: anno.trim(),
        giornata_serie_a_inizio: saInizio,
        giornata_serie_a_fine: saFine,
        budget_stagionale_milioni: budgetStag,
        budget_invernale_milioni: budgetInv,
        n_squadre: nSquadre,
        nome_squadra_fondatore: nomeSquadra.trim() || null,
      });
      setNome("");
      setNomeSquadra("");
      setEsito({
        tipo: "ok",
        testo: tr(
          'Lega "{{v1}}" creata con {{v2}} squadre e la stagione {{v3}}. Codice di ingresso: {{v4}}',
          { v1: creata.lega.nome, v2: creata.n_squadre, v3: creata.anno, v4: creata.codice_invito },
        ),
      });
      await cambia(creata.lega.id, "/leghe");
    } catch (err) {
      setEsito({ tipo: "errore", testo: messaggioErrore(err) });
    }
  }

  async function onEntra(e: React.FormEvent) {
    e.preventDefault();
    setEsito(null);
    try {
      const ingresso = await entra.mutateAsync({
        codice: codice.trim(),
        nome_squadra: nomeSquadraIngresso.trim() || null,
      });
      setCodice("");
      setNomeSquadraIngresso("");
      setEsito({
        tipo: "ok",
        testo: ingresso.squadra_nome
          ? tr('Sei entrato in "{{v1}}" con la squadra "{{v2}}".', {
              v1: ingresso.lega.nome,
              v2: ingresso.squadra_nome,
            })
          : tr('Sei entrato in "{{v1}}" (nessuna squadra libera: chiedi al presidente).', {
              v1: ingresso.lega.nome,
            }),
      });
      await cambia(ingresso.lega.id, "/leghe");
    } catch (err) {
      setEsito({ tipo: "errore", testo: messaggioErrore(err) });
    }
  }

  return (
    <MobileShell title={tr("Le mie leghe")}>
      <div className="space-y-4">
        {esito && (
          <div
            className={`card text-sm ${
              esito.tipo === "ok"
                ? "border-success/40 text-success"
                : "border-danger/40 text-danger"
            }`}
          >
            {esito.testo}
          </div>
        )}

        {/* Le leghe di cui faccio parte */}
        <div className="card space-y-3">
          <h2 className="font-display text-xl text-accent">{tr("Le mie leghe")}</h2>
          {isLoading && <div className="text-sm text-fg-muted">{tr("Caricamento…")}</div>}
          {mie && mie.length === 0 && (
            <div className="text-sm text-fg-muted">
              {tr("Non fai ancora parte di nessuna lega.")}
            </div>
          )}
          {mie?.map((m) => {
            const attuale = m.lega.id === user?.lega_corrente?.id;
            return (
              <div
                key={m.lega.id}
                className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${
                  attuale
                    ? "border-accent/60 bg-primary-deep/60"
                    : "border-primary/20 bg-primary-deep/40"
                }`}
              >
                <div className="min-w-0">
                  <div className="font-bold text-fg truncate">
                    {m.lega.nome}
                    {attuale && <span className="pill ml-2 text-[10px]">{tr("corrente")}</span>}
                  </div>
                  <div className="text-[11px] text-fg-muted">
                    {etichetta(m.membro.ruolo_lega)}
                    {m.membro.ruoli_aggiuntivi.length > 0 && (
                      <> · +{m.membro.ruoli_aggiuntivi.map(etichetta).join(", ")}</>
                    )}
                    {m.membro.is_fondatore && <> {tr("· fondatore")}</>}
                  </div>
                </div>
                {!attuale && (
                  <button
                    type="button"
                    className="btn-ghost btn-sm whitespace-nowrap"
                    onClick={() => void cambia(m.lega.id)}
                  >
                    {tr("Vai →")}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Codice di ingresso della lega corrente (presidente / admin) */}
        {corrente && isAdminCorrente && (
          <div className="card space-y-2">
            <h3 className="font-display text-lg text-accent">
              Codice di ingresso · {corrente.lega.nome}
            </h3>
            <div className="text-sm text-fg-muted">
              Chi ha già un account entra nella lega con questo codice e prende la prima squadra
              libera ({corrente.n_squadre_libere} su {corrente.n_squadre}). Membri:{" "}
              {corrente.n_membri}.
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <code className="font-mono text-lg tracking-widest text-fg bg-primary-deep border border-primary-soft/40 rounded px-3 py-1">
                {corrente.codice_invito ?? tr("— nessun codice —")}
              </code>
              {isPresidenteCorrente && (
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  disabled={rigenera.isPending}
                  onClick={() => rigenera.mutate()}
                >
                  {corrente.codice_invito ? tr("Rigenera") : tr("Genera codice")}
                </button>
              )}
            </div>
            {corrente.lega.is_default && !corrente.codice_invito && (
              <div className="text-[11px] text-fg-muted">
                {tr("La lega è privata: senza codice si entra solo su invito.")}
              </div>
            )}
          </div>
        )}

        {/* Entra con un codice */}
        <form onSubmit={onEntra} className="card space-y-3">
          <h3 className="font-display text-lg text-accent">{tr("Entra in una lega")}</h3>
          <div>
            <label className={LABEL}>{tr("Codice della lega")}</label>
            <input
              className={`${INPUT} font-mono tracking-widest uppercase`}
              value={codice}
              onChange={(e) => setCodice(e.target.value)}
              placeholder="ABCD-EFGH"
              required
            />
          </div>
          <div>
            <label className={LABEL}>{tr("Nome della tua squadra (facoltativo)")}</label>
            <input
              className={INPUT}
              value={nomeSquadraIngresso}
              onChange={(e) => setNomeSquadraIngresso(e.target.value)}
              maxLength={120}
            />
          </div>
          <button type="submit" className="btn-primary" disabled={entra.isPending}>
            {entra.isPending ? tr("Un attimo…") : tr("Entra")}
          </button>
        </form>

        {/* Crea una lega */}
        <form
          ref={formCreaRef}
          onSubmit={onCrea}
          className={`card space-y-3 ${daRegistrazione ? "border-accent/60" : ""}`}
        >
          {daRegistrazione && (
            <div className="rounded-lg bg-accent/15 border border-accent/40 p-3 text-sm text-fg">
              {tr(
                tr(
                  "🎉 Account creato! Ora dai un nome alla tua lega e alla tua squadra: quando la crei ricevi il codice da mandare ai tuoi amici.",
                ),
              )}
            </div>
          )}
          <h3 className="font-display text-lg text-accent">{tr("Crea una nuova lega")}</h3>
          <div className="text-sm text-fg-muted">
            {tr(
              tr(
                "Ne diventi presidente e fondatore: parte subito con la tua squadra, le squadre libere per chi entrerà col codice e la prima stagione. Regolamento e competizioni si impostano poi dal menu Admin.",
              ),
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className={LABEL}>{tr("Nome della lega")}</label>
              <input
                className={INPUT}
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                minLength={3}
                maxLength={60}
                required
                autoFocus={daRegistrazione}
              />
            </div>
            <div>
              <label className={LABEL}>{tr("La tua squadra")}</label>
              <input
                className={INPUT}
                value={nomeSquadra}
                onChange={(e) => setNomeSquadra(e.target.value)}
                maxLength={120}
                placeholder={tr("Squadra 1")}
              />
            </div>
            <div>
              <label className={LABEL}>{tr("Numero squadre")}</label>
              <input
                type="number"
                className={INPUT}
                value={nSquadre}
                min={4}
                max={10}
                onChange={(e) => setNSquadre(Number(e.target.value))}
              />
            </div>
            <div>
              <label className={LABEL}>{tr("Modalità")}</label>
              <select
                className={INPUT}
                value={modalita}
                onChange={(e) => setModalita(e.target.value as "mantra" | "classic")}
              >
                <option value="mantra">{NOME_MODALITA_PRO}</option>
                <option value="classic">Classic</option>
              </select>
            </div>
            <div>
              <label className={LABEL}>{tr("Stagione")}</label>
              <input
                className={INPUT}
                value={anno}
                onChange={(e) => setAnno(e.target.value)}
                placeholder="2026-27"
                pattern="\d{4}[/-]\d{2}"
                required
              />
            </div>
          </div>
          {/* Andrea 2026-09-17: Mantra e' di Fantacalcio.it, va detto. */}
          {modalita === "mantra" && <AvvisoMantra />}
          <div className="text-xs text-fg-muted">
            {tr("La stagione parte dalla")} <b className="text-fg">giornata {saInizio}</b> di Serie
            A{infoReg?.prossima_giornata_sa === saInizio ? tr(" (la prossima da giocare)") : ""}: si
            cambia nelle opzioni avanzate.
          </div>
          <button
            type="button"
            className="text-xs text-fg-muted underline"
            onClick={() => setMostraAvanzate((v) => !v)}
          >
            {mostraAvanzate
              ? tr("Nascondi opzioni avanzate")
              : tr("Opzioni avanzate (giornate, budget)")}
          </button>
          {mostraAvanzate && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className={LABEL}>{tr("Giornata SA inizio")}</label>
                <input
                  type="number"
                  className={INPUT}
                  value={saInizio}
                  min={1}
                  max={38}
                  onChange={(e) => {
                    saToccato.current = true;
                    setSaInizio(Number(e.target.value));
                  }}
                />
              </div>
              <div>
                <label className={LABEL}>{tr("Giornata SA fine")}</label>
                <input
                  type="number"
                  className={INPUT}
                  value={saFine}
                  min={1}
                  max={38}
                  onChange={(e) => setSaFine(Number(e.target.value))}
                />
              </div>
              <div>
                <label className={LABEL}>{tr("Budget stagionale (M)")}</label>
                <input
                  type="number"
                  className={INPUT}
                  value={budgetStag}
                  min={1}
                  onChange={(e) => setBudgetStag(Number(e.target.value))}
                />
              </div>
              <div>
                <label className={LABEL}>{tr("Budget invernale (M)")}</label>
                <input
                  type="number"
                  className={INPUT}
                  value={budgetInv}
                  min={0}
                  onChange={(e) => setBudgetInv(Number(e.target.value))}
                />
              </div>
            </div>
          )}
          <button type="submit" className="btn-primary" disabled={crea.isPending}>
            {crea.isPending ? tr("Creazione…") : tr("Crea la lega")}
          </button>
        </form>
      </div>
    </MobileShell>
  );
}
